import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Button } from "@/components/ui/button";
import api from "@/lib/axios";
import { useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { useQuery } from "@tanstack/react-query";
import FormFilter from "./FormFilter";
import { useSearchParams } from "react-router-dom";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import { RiFileExcel2Fill } from "react-icons/ri";

const getDataRekapPendaftaranInternasional = async (
  idsUkParams?: string,
  idsSysPeriodeParams?: string,
  tahun?: string,
) => {
  const params = new URLSearchParams();
  if (idsUkParams) params.append("ids_uk", idsUkParams);
  if (idsSysPeriodeParams)
    params.append("ids_sys_periode", idsSysPeriodeParams);
  if (tahun) params.append("tahun", tahun);

  const url = params.toString()
    ? `/operator-internasional/dashboard/rekap-pendaftaran?${params.toString()}`
    : "/operator-internasional/dashboard/rekap-pendaftaran";

  const response = await api.get(url);
  return response.data?.data;
};

type DataRekapPendaftaran = {
  jalur_jenis: string;
  gelombang: string;
  fakultas_nama: string;
  jurusan_nama: string;
  prodi_nama: string;
  jumlah_pendaftar: number;
  jumlah_validasi: number;
  jumlah_lulus: number;
  jumlah_daftar_kembali: number;
};

export default function TabelRekap({ tahun }: { tahun?: string }) {
  const { apiBaseUrl } = useApiBaseUrl();
  const [searchParams] = useSearchParams();
  const idsUkParams = searchParams.get("ids_uk") || "";
  const idsSysPeriodeParams = searchParams.get("ids_sys_periode") || "";
  const idsJalurJenisParams = searchParams.get("ids_ref_jalur_jenis") || "";

  const {
    data: dataRekapPendaftaran,
    isPending: isPendingDataRekapPendaftaran,
  } = useQuery({
    queryKey: [
      "dataRekapPendaftaranInternasional",
      idsUkParams,
      idsSysPeriodeParams,
      tahun,
    ],
    queryFn: () =>
      getDataRekapPendaftaranInternasional(
        idsUkParams,
        idsSysPeriodeParams,
        tahun,
      ),
  });

  const [showFilter, setShowFilter] = useState(false);

  if (isPendingDataRekapPendaftaran) return <SkeletonTable />;

  return (
    <div>
      <div className="mb-3 flex gap-3 items-center">
        <h1 className="text-xl font-bold">Tabel Rekap</h1>
        <Button
          variant={"outline"}
          className="flex items-center gap-x-3"
          onClick={() => setShowFilter((show) => !show)}
        >
          Filter <LuListFilter />
        </Button>
        <Button
          variant={"outline"}
          className="flex items-center gap-x-3"
          onClick={() => {
            if (idsJalurJenisParams)
              searchParams.set("ids_ref_jalur_jenis", idsJalurJenisParams);
            if (idsUkParams) searchParams.set("ids_uk", idsUkParams);
            if (idsSysPeriodeParams)
              searchParams.set("ids_sys_periode", idsSysPeriodeParams);

            window.open(
              `${apiBaseUrl}/operator-internasional/dashboard/rekap-pendaftaran/export?${searchParams?.toString()}`,
            );
          }}
        >
          Export <RiFileExcel2Fill />
        </Button>
      </div>
      <div className="flex-1 overflow-auto rounded-xl border">
        <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
          <thead>
            <tr>
              <th className="py-1 px-2 border font-semibold text-start">
                Jalur
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Fakultas
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Gelombang
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Program Studi
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Peminat
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Sudah Validasi
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Lulus
              </th>
              <th className="py-1 px-2 border font-semibold text-start">
                Sudah Daftarkembali
              </th>
            </tr>
          </thead>
          <tbody>
            {dataRekapPendaftaran?.rekap?.length > 0
              ? dataRekapPendaftaran?.rekap.map(
                  (item: DataRekapPendaftaran, index: number) => {
                    return (
                      <tr key={index}>
                        <td className="py-1 px-2 border">{item.jalur_jenis}</td>
                        <td className="py-1 px-2 border">
                          {item.fakultas_nama}
                        </td>
                        <td className="py-1 px-2 border text-center">
                          {item.gelombang}
                        </td>
                        <td className="py-1 px-2 border">{item.prodi_nama}</td>
                        <td className="py-1 px-2 border text-center">
                          {item.jumlah_pendaftar}
                        </td>
                        <td className="py-1 px-2 border text-center">
                          {item.jumlah_validasi}
                        </td>
                        <td className="py-1 px-2 border text-center">
                          {item.jumlah_lulus}
                        </td>
                        <td className="py-1 px-2 border text-center">
                          {item.jumlah_daftar_kembali}
                        </td>
                      </tr>
                    );
                  },
                )
              : ""}
          </tbody>
          <tfoot>
            <tr>
              <td
                className="py-1 px-2 border font-semibold text-center"
                colSpan={4}
              >
                Total
              </td>
              <td className="py-1 px-2 border text-center">
                {dataRekapPendaftaran?.total?.total_pendaftar}
              </td>
              <td className="py-1 px-2 border text-center">
                {dataRekapPendaftaran?.total?.total_validasi}
              </td>
              <td className="py-1 px-2 border text-center">
                {dataRekapPendaftaran?.total?.total_lulus}
              </td>
              <td className="py-1 px-2 border text-center">
                {dataRekapPendaftaran?.total?.total_daftar_kembali}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Modal show={showFilter} onClose={() => setShowFilter(false)} width="2xl">
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  );
}
